import React, { useEffect, useRef, useState } from 'react'
import { uniqBy } from 'lodash'

// Const
import { MODAL_CONSTS } from '../../../const/modal-const'
import { CONST } from '../../../const/const'

// Modals
import MainModal from '../../modals/common/MainModal'
import { ModalBackButton } from '../../modals/components/ModalBackButton'

// Models
import { getConvertedData, getSelectedUserAsTeamMember } from '../../../models/interface.helper'
import { IUserHorseMappingInterface } from '../../../models/user-horse-mapping/userHorseMapping.interface'
import { IUserInterface } from '../../../models/users/user.interface'
import { HorseModel } from '../../../models/horse/horse.model'
import { IHorseData } from '../../../models/horse/horse.interface'

// Store
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectHorses, setHorses } from '../../../store/horses/horseSlice'
import { selectTeamTabData, setTeamTabData } from '../../../store/registration/registrationSlice'
import {
  riderProfileRegistrationActions,
  selectRiderProfileNewTeamRider,
  selectRiderProfileSearch,
  selectRiderProfileType,
} from '../../../store/riderProfileRegistration/riderProfileRegistrationSlice'

// Components
import { IconPlus } from '../../icons/IconPlus'
import { IUserExtendedWithSelected, SearchUsers } from '../../modals/components/SearchUsers'

// Services
import FirestoreService from '../../../services/firestoreService'
import UserService from '../../../services/firebase-service/users/userService'

// Hooks
import useHorses from '../../../hooks/users/competitor/profile/useHorses'

import { ECompetitorTypes } from '../../../types/competitor_types'
import { ITeamMemberR } from '../../../models/event-registered-users/event-registered-users.interface'
import { ITeamTab } from '../../../models/event-registered-users/event-registered-users.interface'

type Props = {
  show: boolean
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
  dataToPassOn: {
    competitor: IUserInterface
    userTeamMembers: IUserInterface[]
    onSave?: (teamTabData: ITeamTab) => Promise<void>
  }
}

const RegistrationTeamTabModal = ({ dataToPassOn, handleModal }: Props) => {
  const dispatch = useAppDispatch()
  const { userTeamMembers, competitor } = dataToPassOn
  const riderProfileType = useAppSelector(selectRiderProfileType)
  const riderProfileNewTeamRider = useAppSelector(selectRiderProfileNewTeamRider)
  const riderProfileSearch = useAppSelector(selectRiderProfileSearch)

  const teamMembers = useAppSelector(selectTeamTabData)
  const myHorses = useAppSelector(selectHorses)

  const searchBoxRef = useRef<HTMLInputElement>(null)
  const [listToShow, setListToShow] = useState<IUserExtendedWithSelected[]>([])
  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)

  const { getMapping } = useHorses()

  const focusInput = () => {
    if (searchBoxRef.current) searchBoxRef.current?.focus()
  }

  const handleUsers = async (strToSearch: string) => {
    dispatch(riderProfileRegistrationActions.setSearch(strToSearch))

    const search = strToSearch.toLowerCase().replace(/\s+/g, '')

    try {
      setSearchLoading(true)
      const currentSelected = listToShow?.filter((item) => item.selected)

      if (search.length < 3) {
        setListToShow(uniqBy([...currentSelected, ...userTeamMembers], 'id'))
        return
      }

      const usersInDb = await UserService.getByName(search)
      const matched = usersInDb.filter((user) => competitor?.id !== user.id)
      setListToShow(uniqBy([...currentSelected, ...matched], 'id'))
    } catch (error) {
      console.error('error: ', error)
    } finally {
      setSearchLoading(false)
    }
  }

  async function onSubmit() {
    const teamData: ITeamMemberR[] = []

    setLoading(true)
    try {
      if (riderProfileType === ECompetitorTypes.teamRider) {
        const selectedUsers: IUserExtendedWithSelected[] = []

        listToShow?.forEach((user) => {
          user.selected && selectedUsers.push(user)
        })

        dispatch(riderProfileRegistrationActions.setTeam(selectedUsers))
        handleModal(false, MODAL_CONSTS.REGISTER_TEAM)

        if (riderProfileNewTeamRider) {
          handleModal(true, MODAL_CONSTS.ADD_NEW_TEAM_MEMBER)
        } else {
          handleModal(true, MODAL_CONSTS.CONFIRM_PROFILE_DETAILS)
        }
      } else {
        listToShow?.forEach((user) => {
          if (user.selected) {
            teamData.push(getConvertedData(getSelectedUserAsTeamMember(user, competitor)))
          }
        })

        const filteredTeamData = teamData.filter(
          (member) => !teamMembers.find((m) => m.memberId === member.memberId)
        )

        const newIds: string[] = []

        filteredTeamData.forEach((m) => {
          if (m.memberId) {
            newIds.push(m.memberId)
          }
        })

        if (newIds[0]) {
          const promises: IUserHorseMappingInterface[][] = []

          for (const id of newIds) {
            const { horsesTeamsMappings } = await getMapping(id)
            promises.push(horsesTeamsMappings)
          }

          const horses: IUserHorseMappingInterface[] = []
          const snaps = await Promise.all(promises)
          snaps.forEach((sn) => horses.push(...sn))
          const horsesIds: string[] = []

          horses.forEach((h) => {
            h.horseId &&
              h.horseOwnerId &&
              newIds?.includes(h.horseOwnerId) &&
              horsesIds.push(h.horseId)
          })

          if (horsesIds[0]) {
            const membersHorses: IHorseData[] = []
            const horsesSnaps = await FirestoreService.getItemsUsingIds(
              CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.HORSES.NAME,
              horsesIds
            )

            horsesSnaps.forEach((currDoc) => {
              membersHorses.push(getConvertedData(HorseModel.fromFirestoreDoc(currDoc).toObject()))
            })

            dispatch(setHorses([...myHorses, ...membersHorses]))
          }
        }

        dispatch(setTeamTabData([...teamData]))
        await dataToPassOn?.onSave?.(teamData)
        dispatch(riderProfileRegistrationActions.clear())
        handleModal(false, MODAL_CONSTS.REGISTER_TEAM)
      }
    } catch (error) {
      console.error(error, 'error')
    } finally {
      setLoading(false)
    }
  }

  const onToggleSelect = (team_member: IUserExtendedWithSelected) => {
    if (listToShow) {
      const sameTypeUsersArrCopy = listToShow.map((currUser) => {
        if (currUser.id === team_member?.id) {
          return { ...currUser, selected: !currUser.selected }
        } else {
          return currUser
        }
      })
      setListToShow([...sameTypeUsersArrCopy])
    }
  }

  useEffect(() => {
    const list: IUserExtendedWithSelected[] = []
    userTeamMembers?.forEach((user) => {
      const isSelected = teamMembers.find((member) => member.memberId === user.id)

      if (isSelected) {
        list.push({ ...user, selected: true })
      } else {
        list.push(user)
      }
    })
    setListToShow(list)
  }, [teamMembers, userTeamMembers])

  const teamRiderBackHandler = () => {
    handleModal(false, MODAL_CONSTS.REGISTER_TEAM)
    handleModal(true, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER, {
      backHandler: () => {
        handleModal?.(false, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER)
        handleModal?.(true, MODAL_CONSTS.CREATE_RIDER_PROFILE_STEP_2)
      },
    })
  }

  const listToShowFiltered = listToShow?.filter((member) => member?.id !== competitor?.id)

  return (
    <MainModal
      title="Add team member"
      description={'Add team members so you can include them in your registration'}
      show={true}
      type="REGISTER_TEAM"
      goBackBtn={
        riderProfileType === ECompetitorTypes.teamRider && (
          <ModalBackButton onClick={teamRiderBackHandler} />
        )
      }
      size="md"
      onTransitionEnd={focusInput}
      titleClassName="!font-normal"
      buttons={[
        {
          label: riderProfileType === ECompetitorTypes.teamRider ? 'NEXT >' : 'SAVE',
          bgClass: 'bg-SeabiscuitMainThemeColor',
          onClick: onSubmit,
          className: 'outline-none',
          textClass: 'text-white',
          disabled: (!listToShowFiltered?.[0] && !riderProfileNewTeamRider) || searchLoading,
          loading: loading,
          fullWidth: true,
        },
        {
          label: 'CANCEL',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          className: 'outline-none',
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitLightTextColor',
          disabled: searchLoading,
          onClick: () => {
            dispatch(riderProfileRegistrationActions.clear())
            handleModal(false, MODAL_CONSTS.REGISTER_TEAM)
          },
          loading: loading,
          fullWidth: true,
        },
      ]}
    >
      <div className="inputSearch mt-4 flex items-center relative">
        <input
          ref={searchBoxRef}
          type="text"
          onChange={(e) => handleUsers(e.target.value)}
          value={riderProfileSearch}
          placeholder="Enter at least 3 letters..."
          className="rounded-full w-full border focus:border-[#D3DAEE] border-[#D3DAEE] text-SeabiscuitDark200ThemeColor focus:ring-0"
        />
        <div className="absolute right-0 top-0 h-full w-[135px] flex justify-center items-center">
          <span className="absolute left-0 h-[60%] top-[50%] translate-y-[-50%] w-[1px] bg-[#122B4680]" />
          <button
            onClick={() => {
              handleModal(false, MODAL_CONSTS.REGISTER_TEAM)
              handleModal(true, MODAL_CONSTS.ADD_NEW_TEAM_MEMBER)
            }}
            className={
              'group text-[#122B4680] text-[14px] flex items-center gap-1 hover:text-SeabiscuitMainThemeColor'
            }
          >
            <IconPlus /> New Person
          </button>
        </div>
      </div>
      <SearchUsers
        loading={searchLoading}
        users={listToShowFiltered}
        searchValue={riderProfileSearch}
        onToggleSelect={onToggleSelect}
      />
    </MainModal>
  )
}

export default RegistrationTeamTabModal
